import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, MapPin, Clock, DollarSign, ArrowRight, Sparkles } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import { budgetLabel } from "@/components/servicerequest/ReviewStep";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const TABS = [
  { key: "new,viewed", label: "New matches" },
  { key: "estimated", label: "Estimates sent" },
  { key: "won", label: "Won" },
  { key: "declined,expired,closed,lost", label: "Closed" },
];
const STATUS_STYLE = { new: "bg-[#d97757] text-white", viewed: "bg-[#165a71]/10 text-[#165a71]", estimated: "bg-amber-100 text-amber-800", won: "bg-emerald-100 text-emerald-800" };

/** Provider "Job matches" — opportunities the matching engine sent to this pro. */
export default function ProviderFindWorkPage() {
  const [tab, setTab] = useState(TABS[0].key);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  useDocumentTitle("Job matches");

  useEffect(() => {
    setLoading(true);
    api.get("/service-requests/opportunities/mine", { params: { status: tab } }).then((r) => setData(r.data)).catch(() => setData({ items: [], counts: {} })).finally(() => setLoading(false));
  }, [tab]);
  const count = (k) => k.split(",").reduce((s, x) => s + (data.counts[x] || 0), 0);

  return (
    <DashboardShell>
      <div className="max-w-5xl" data-testid="opportunities-page">
        <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Job matches</div>
        <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Customers matched to you</h1>
        <p className="text-[#252A34]/70 text-sm mt-1">Requests are sent to a small group of best-fit pros. Respond quickly to win more work.</p>
        <div className="mt-6 flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${tab === t.key ? "bg-[#071b22] text-white" : "bg-white border border-slate-200 text-[#252A34]/80"}`} data-testid={`opp-tab-${t.label.toLowerCase().replace(/ /g, "-")}`}>
              {t.label} <span className="opacity-60 tabular-nums">{count(t.key)}</span>
            </button>
          ))}
        </div>
        <div className="mt-5">
          {loading ? <div className="text-[#252A34]/60">Loading…</div> : data.items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="opp-empty">
              <Send className="h-6 w-6 mx-auto text-[#165a71]" />
              <div className="mt-2 font-display text-lg text-[#071b22] font-semibold">Nothing here yet.</div>
              <div className="text-sm text-[#252A34]/70 mt-1">Keep your categories and service area up to date to get matched.</div>
            </div>
          ) : (
            <div className="grid gap-3">
              {data.items.map((o) => (
                <Link key={o.id} to={`/dashboard/opportunities/${o.request_id}`} className="rounded-2xl border border-slate-200 bg-white p-5 flex items-start gap-4 hover:border-[#165a71]/40 transition-colors" data-testid={`opp-row-${o.id}`}>
                  <div className="h-12 w-12 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71] font-display font-semibold tabular-nums">{Math.round(o.match_score)}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-display font-semibold text-[#071b22] truncate">{o.request.title}</div>
                      <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[o.status] || "bg-slate-100 text-slate-600"}`}>{o.status}</span>
                    </div>
                    <div className="text-sm text-[#252A34]/70 mt-1 line-clamp-2">{o.request.ai_summary || o.request.description}</div>
                    <div className="text-xs text-[#252A34]/60 mt-3 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {(o.request.urgency || "").replace(/_/g, " ")}</span>
                      {o.request.address_snapshot?.city && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {o.request.address_snapshot.city}, {o.request.address_snapshot.state}</span>}
                      <span className="inline-flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" /> {budgetLabel(o.request)}</span>
                      {o.admin_assigned ? <span className="inline-flex items-center gap-1 text-[#8a5a00]" data-testid={`opp-recommended-${o.id}`}><Sparkles className="h-3.5 w-3.5" /> Recommended by Gulf Coast Work</span>
                        : o.invited && <span className="inline-flex items-center gap-1 text-[#d97757]"><Sparkles className="h-3.5 w-3.5" /> Invited you</span>}
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-[#165a71] mt-2" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
